export type ToastType = 'error' | 'success' | 'warning' | 'info' | 'alert' | 'confirm';

export interface ToastAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger';
  action: () => void | boolean | Promise<void | boolean>;
}

export interface ToastOptions {
  duration?: number;
  closable?: boolean;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center' | 'center';
  modal?: boolean;
  backdrop?: boolean;
  actions?: ToastAction[];
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  closable: boolean;
  position: ToastOptions['position'];
  createdAt: number;
  modal?: boolean;
  backdrop?: boolean;
  actions?: ToastAction[];
}

class ToastService {
  private toasts: Map<string, Toast> = new Map();
  private listeners: Set<(toasts: Toast[]) => void> = new Set();
  private resolvers: Map<string, (value: any) => void> = new Map();

  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private notifyListeners(): void {
    const toastArray = Array.from(this.toasts.values());
    this.listeners.forEach(listener => listener(toastArray));
  }

  show(message: string, type: ToastType = 'info', options: ToastOptions = {}): string {
    const toast: Toast = {
      id: this.generateId(),
      type,
      message,
      duration: options.duration ?? (type === 'error' ? 5000 : type === 'alert' || type === 'confirm' ? 0 : 3000),
      closable: options.closable ?? (type === 'alert' || type === 'confirm' ? false : true),
      position: options.position ?? (type === 'alert' || type === 'confirm' ? 'center' : 'top-right'),
      createdAt: Date.now(),
      modal: options.modal ?? (type === 'alert' || type === 'confirm'),
      backdrop: options.backdrop ?? (type === 'alert' || type === 'confirm'),
      actions: options.actions
    };
    
    this.toasts.set(toast.id, toast);
    
    // Auto-hide after duration
    if (toast.duration > 0) {
      setTimeout(() => {
        this.hide(toast.id);
      }, toast.duration);
    }
    
    this.notifyListeners();
    return toast.id;
  }

  hide(id: string): void {
    const toast = this.toasts.get(id);
    if (!toast) return;
    
    this.toasts.delete(id);
    this.notifyListeners();
  }

  clear(): void {
    this.toasts.clear();
    this.notifyListeners();
  }

  // Action handlers for alert/confirm toasts
  resolveAction(id: string, result: any): void {
    const resolver = this.resolvers.get(id);
    if (resolver) {
      resolver(result);
      this.resolvers.delete(id);
    }
    this.hide(id);
  }

  // Convenience methods
  error(message: string, options?: ToastOptions): string {
    return this.show(message, 'error', options);
  }

  success(message: string, options?: ToastOptions): string {
    return this.show(message, 'success', options);
  }

  warning(message: string, options?: ToastOptions): string {
    return this.show(message, 'warning', options);
  }

  info(message: string, options?: ToastOptions): string {
    return this.show(message, 'info', options);
  }
  
  alert(message: string, options?: ToastOptions): Promise<void> {
    return new Promise((resolve) => {
      const defaultActions: ToastAction[] = options?.actions || [
        { 
          label: 'OK', 
          variant: 'primary', 
          action: () => {
            resolve();
            return true;
          }
        }
      ];
      
      const id = this.show(message, 'alert', {
        ...options,
        actions: defaultActions
      });
      
      this.resolvers.set(id, resolve);
    });
  }
  
  confirm(message: string, options?: ToastOptions): Promise<boolean> {
    return new Promise((resolve) => {
      const defaultActions: ToastAction[] = options?.actions || [
        { 
          label: 'Cancel', 
          variant: 'secondary', 
          action: () => {
            resolve(false);
            return false;
          }
        },
        { 
          label: 'OK', 
          variant: 'primary', 
          action: () => {
            resolve(true);
            return true;
          }
        }
      ];
      
      const id = this.show(message, 'confirm', {
        ...options,
        actions: defaultActions
      });
      
      this.resolvers.set(id, resolve);
    });
  }

  // For React components to subscribe to toast changes
  subscribe(listener: (toasts: Toast[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getToasts(): Toast[] {
    return Array.from(this.toasts.values());
  }
}

// Export singleton instance
export const toastService = new ToastService();

// Export default for easier importing
export default toastService; 